// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Icon, { IconWrapper } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { color, getSystemColor, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends Omit<TypographyProps, "type"> {
    type?: "success" | "danger" | "warning" | "info";
    fullWidth?: boolean;
    showClose?: boolean;
}

type Props = OwnProps;

/**
 * change icon to data:image/svg+xml
 * @param type
 */
const getNotifyIcons = (type: OwnProps["type"]) => {
    const iconColor = getSystemColor(`${type}.main`, 1, true).replace("#", "%23");
    switch (type) {
        case "success":
            return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='${iconColor}' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' focusable='false' width='24' height='24'%3E%3Cpath d='M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z'/%3E%3C/svg%3E`;
        case "danger":
            return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='${iconColor}' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' focusable='false' width='24' height='24'%3E%3Cpath d='M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm121.6 313.1c4.7 4.7 4.7 12.3 0 17L338 377.6c-4.7 4.7-12.3 4.7-17 0L256 312l-65.1 65.6c-4.7 4.7-12.3 4.7-17 0L134.4 338c-4.7-4.7-4.7-12.3 0-17l65.6-65-65.6-65.1c-4.7-4.7-4.7-12.3 0-17l39.6-39.6c4.7-4.7 12.3-4.7 17 0l65 65.7 65.1-65.6c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17L312 256l65.6 65.1z'/%3E%3C/svg%3E`;
        case "warning":
            return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512' fill='${iconColor}' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' focusable='false' width='24' height='24'%3E%3Cpath d='M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0l239.94 416.028zM288 354c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z'/%3E%3C/svg%3E`;
        case "info":
            return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='${iconColor}' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' focusable='false' width='24' height='24'%3E%3Cpath d='M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z'/%3E%3C/svg%3E`;
    }
};

export const getNotifyStyle = (type: OwnProps["type"], fullWidth = false): TypographyProps => {
    return {
        role: "status",
        "aria-live": "polite",
        "data-test-selector": `notify-${type}`,
        css: css`
            display: flex;
            align-items: flex-start;
            padding: 12px;
            text-align: left;
            border: 1px solid ${color(`${type}.main`)};
            background-color: ${color(`${type}.light`)};
            border-radius: 2px;
            ${fullWidth
                ? css`
                      width: 100%;
                      max-width: 100%;
                  `
                : css`
                      width: 100%;
                      max-width: 600px;
                  `}

            min-width: 280px;
            margin: 0 0 20px;

            font-size: 12px;
            font-weight: normal;
            letter-spacing: 0;
            line-height: 23px;
            white-space: break-spaces;
            word-break: break-word;
            overflow-wrap: break-word;

            &:before {
                display: inline-block;
                width: 24px;
                height: 24px;
                min-width: 24px;
                margin-right: 8px;
                content: " ";
                background-repeat: no-repeat;
                transform: scale(0.75);
                background-image: url("${getNotifyIcons(type)}");
            }

            ${IconWrapper} {
                margin-left: auto;
                cursor: pointer;
                color: ${color("common.ui5")};
                margin-top: 5px;
                &:hover {
                    color: ${color("danger")};
                }
            }

            ${xScreen("lte", "xs")} {
                max-width: none;
                min-width: initial;
            }

            @media print {
                -webkit-print-color-adjust: exact !important; /* Chrome, Safari 6 – 15.3, Edge */
                color-adjust: exact !important; /* Firefox 48 – 96 */
                print-color-adjust: exact !important; /* Firefox 97+, Safari 15.4+ */
            }
        `,
    };
};

const Notify: FC<Props> = ({
    type = "info",
    fullWidth,
    className,
    children,
    css: extendedCssStyle,
    showClose = false,
}) => {
    const [styles] = React.useState(() => mergeWithCss(getNotifyStyle(type, fullWidth), { css: extendedCssStyle }));
    const [visible, setVisibility] = React.useState(true);

    if (!React.Children.count(children) || !visible) {
        return null;
    }

    const handleCloseNotify = () => {
        setVisibility(false);
    };

    const classNames = ["notify", className || ""].join(" ").trim();
    return (
        <Typography {...styles} className={classNames}>
            {children}
            {showClose && <Icon src={X} size={14} color="common.ui5" onClick={handleCloseNotify} />}
        </Typography>
    );
};

export default Notify;
